"use client";

import { cn } from "@/lib/utils";

interface TerminalPanelHeaderProps {
  /** Panel code/ID displayed on the left (e.g., "PNL.001" or "WATCHLIST") */
  code?: string;
  /** Title displayed in the center */
  title: string;
  /** Status indicator on the right */
  status?: "live" | "idle" | "streaming";
  /** Additional className for customization */
  className?: string;
}

/**
 * Bloomberg-style terminal panel header with window chrome
 *
 * Features:
 * - Colored window control dots (like macOS)
 * - Panel code on the left
 * - Title in the center
 * - Status indicator on the right
 */
export function TerminalPanelHeader({
  code,
  title,
  status = "live",
  className,
}: TerminalPanelHeaderProps) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-2 px-3 py-1.5",
        "border-b border-[var(--card-border)]",
        "bg-[var(--accent-glow)]",
        className
      )}
    >
      {/* Left section: Window dots + Panel code */}
      <div className="flex items-center gap-2.5">
        {/* Window control dots */}
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-[var(--accent-primary)]/40" />
          <span className="w-2 h-2 rounded-full bg-[var(--accent-primary)]/30" />
          <span className="w-2 h-2 rounded-full bg-[var(--accent-primary)]/20" />
        </div>

        {/* Panel code */}
        {code && (
          <span
            className={cn(
              "px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em]",
              "text-[var(--text-accent)] bg-[var(--accent-primary)]/10",
              "border border-[var(--accent-primary)]/30"
            )}
          >
            {code}
          </span>
        )}
      </div>

      {/* Center: Title */}
      <div className="flex-1 text-center">
        <span
          className={cn(
            "font-mono text-[10px] font-medium uppercase tracking-[0.15em]",
            "text-[var(--text-secondary)]",
            "[text-shadow:0_0_10px_var(--accent-glow)]"
          )}
        >
          {title}
        </span>
      </div>

      {/* Right: Status indicator */}
      <div className="flex items-center gap-1.5">
        {status === "live" && (
          <>
            <span className="relative flex h-1.5 w-1.5">
              <span
                className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[var(--accent-primary)]"
                style={{ boxShadow: "0 0 6px var(--glow-color)" }}
              />
            </span>
            <span className="font-mono text-[8px] text-[var(--text-muted)] uppercase tracking-[0.2em]">
              LIVE
            </span>
          </>
        )}
        {status === "streaming" && (
          <>
            <span
              className="relative flex h-1.5 w-1.5"
            >
              <span
                className="inline-flex rounded-full h-1.5 w-1.5 bg-[var(--accent-primary)]"
                style={{ boxShadow: "0 0 6px var(--glow-color)" }}
              />
            </span>
            <span className="font-mono text-[8px] text-[var(--text-muted)] uppercase tracking-[0.2em]">
              STREAM
            </span>
          </>
        )}
        {status === "idle" && (
          <>
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--text-muted)]/40" />
            <span className="font-mono text-[8px] text-[var(--text-muted)]/60 uppercase tracking-[0.2em]">
              IDLE
            </span>
          </>
        )}
      </div>
    </div>
  );
}

interface MiniTerminalHeaderProps {
  /** Panel code displayed (e.g., "APPS.IDX") */
  code: string;
  /** Additional className for customization */
  className?: string;
}

/**
 * Compact terminal header for smaller cards like stat cards
 * Shows just the code with minimal chrome
 */
export function MiniTerminalHeader({ code, className }: MiniTerminalHeaderProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-1.5 mb-2 pb-1.5",
        "border-b border-[var(--card-border)]",
        className
      )}
    >
      {/* Mini window dots */}
      <div className="flex items-center gap-0.5">
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-primary)]/40" />
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-primary)]/30" />
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-primary)]/20" />
      </div>

      {/* Code */}
      <span
        className={cn(
          "font-mono text-[8px] uppercase tracking-[0.15em]",
          "text-[var(--text-muted)]"
        )}
      >
        [{code}]
      </span>

      {/* Spacer line */}
      <div className="flex-1 h-px bg-gradient-to-r from-[var(--card-border)] to-transparent" />

      {/* Live indicator */}
      <span className="relative flex h-1 w-1">
        <span className="relative inline-flex rounded-full h-1 w-1 bg-[var(--accent-primary)]" style={{ boxShadow: "0 0 4px var(--glow-color)" }} />
      </span>
    </div>
  );
}
